import { useEffect } from 'react'

import type { MeasurementLifecycleOptions } from './useMeasurementLifecycle.types'

export const useMeasurementLifecycle = ({
  enabled,
  frozenRef,
  persistCurrentCheckpoint,
  sendTimeSpent,
  stopTimers,
  syncTimers,
  restoreTrackingView
}: MeasurementLifecycleOptions) => {
  useEffect(() => {
    if (!enabled) return
    const onTransition = () => {
      persistCurrentCheckpoint()
      syncTimers()
    }
    const onFreeze = () => {
      // Account up to this event, then stop. Freeze is an additional signal,
      // not a substitute for the clock-gap fallback on unsupported browsers.
      frozenRef.current = true
      persistCurrentCheckpoint()
      stopTimers()
    }
    const onResume = () => {
      frozenRef.current = false
      persistCurrentCheckpoint(false)
      syncTimers()
    }
    document.addEventListener('visibilitychange', onTransition)
    window.addEventListener('focus', onTransition)
    window.addEventListener('blur', onTransition)
    document.addEventListener('freeze', onFreeze)
    document.addEventListener('resume', onResume)
    // beforeunload can be canceled by the app's unsaved-changes prompt.
    // Finalize only when the page actually leaves, so choosing Stay keeps
    // the current measurement and its timers alive.
    window.addEventListener('pagehide', sendTimeSpent)
    return () => {
      document.removeEventListener('visibilitychange', onTransition)
      window.removeEventListener('focus', onTransition)
      window.removeEventListener('blur', onTransition)
      document.removeEventListener('freeze', onFreeze)
      document.removeEventListener('resume', onResume)
      window.removeEventListener('pagehide', sendTimeSpent)
      stopTimers()
    }
  }, [
    enabled,
    frozenRef,
    persistCurrentCheckpoint,
    sendTimeSpent,
    stopTimers,
    syncTimers
  ])

  useEffect(() => {
    if (!enabled) return
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) restoreTrackingView()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [enabled, restoreTrackingView])
}
